/**
 * Event Permissions Service
 * Manages user access permissions for events
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type {
  EventPermission,
  PermissionLevel,
  AddEventPermissionInput,
  UserEventAccess,
} from "../types/event.types";

/**
 * Get all permissions for an event with user details
 */
export const getEventPermissions = async (
  eventId: UUID,
): Promise<EventPermission[]> => {
  // First get permissions
  const { data: permissions, error: permError } = await supabase
    .from("event_permissions")
    .select("*")
    .eq("event_id", eventId)
    .order("permission_level", { ascending: true })
    .order("created_at", { ascending: true });

  if (permError) {
    console.error("[EventPermissions] Error fetching permissions:", permError);
    throw permError;
  }

  if (!permissions || permissions.length === 0) {
    return [];
  }

  // Get unique user IDs
  const userIds = [
    ...new Set(permissions.map((p) => p.user_id).filter(Boolean)),
  ];
  const grantedByIds = [
    ...new Set(permissions.map((p) => p.granted_by).filter(Boolean)),
  ];
  const allUserIds = [...new Set([...userIds, ...grantedByIds])];

  // Fetch user profiles
  const { data: profiles, error: profileError } = await supabase
    .from("profiles")
    .select("id, full_name, email")
    .in("id", allUserIds);

  if (profileError) {
    console.error("[EventPermissions] Error fetching profiles:", profileError);
    // Return permissions without user details
    return permissions;
  }

  // Create lookup map
  const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);

  // Enrich permissions with user data
  return permissions.map((perm) => ({
    ...perm,
    user: profileMap.get(perm.user_id) || undefined,
    granted_by_user: perm.granted_by
      ? profileMap.get(perm.granted_by)
      : undefined,
  }));
};

/**
 * Get current user's permission for an event
 */
export const getCurrentUserEventPermission = async (
  eventId: UUID,
): Promise<EventPermission | null> => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return null;
  }

  const { data, error } = await supabase
    .from("event_permissions")
    .select("*")
    .eq("event_id", eventId)
    .eq("user_id", user.id)
    .single();

  if (error && error.code !== "PGRST116") {
    // PGRST116 = no rows returned
    console.error("[EventPermissions] Error fetching user permission:", error);
    throw error;
  }

  return data;
};

/**
 * Get user's effective access for an event
 * Considers both explicit permissions and admin role
 */
export const getUserEventAccess = async (
  eventId: UUID,
): Promise<UserEventAccess | null> => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return null;
  }

  // Check if user is admin
  // Note: profiles.id IS the user_id (references auth.users.id)
  const { data: profile } = await supabase
    .from("profiles")
    .select("role")
    .eq("id", user.id)
    .single();

  const isAdmin =
    profile?.role &&
    ["superadmin", "admin"].includes(profile.role.toLowerCase());

  // If admin, they have full access
  if (isAdmin) {
    return {
      event_id: eventId,
      user_id: user.id,
      permission_level: "owner" as PermissionLevel,
      is_owner: true,
      can_edit: true,
      can_manage: true,
    };
  }

  // Migration 2026-10-08-402 (MSR round 9, D13): an MSR may VIEW an event assigned to or shared with them but
  // never edit it, matching has_event_permission(). Their writes go through scoped commands (attendance via
  // set_event_attendance, check-in and rewards via Operations).
  // Migration 414: "assigned" = the event's Sales Reps list (or the legacy msr_id, or a share). Ask the server's
  // own rule instead of re-deriving it here.
  if (profile?.role?.toLowerCase() === "msr") {
    const { data: canView } = await supabase.rpc("has_event_permission", {
      p_event_id: eventId,
      p_user_id: user.id,
      p_required_level: "view",
    });
    if (canView === true) {
      return {
        event_id: eventId,
        user_id: user.id,
        permission_level: "view" as PermissionLevel,
        is_owner: false,
        can_edit: false,
        can_manage: false,
      };
    }
    return null;
  }

  // Check explicit permission
  const permission = await getCurrentUserEventPermission(eventId);

  if (permission) {
    return {
      event_id: eventId,
      user_id: user.id,
      permission_level: permission.permission_level,
      is_owner: permission.permission_level === "owner",
      can_edit: ["owner", "edit"].includes(permission.permission_level),
      can_manage: permission.permission_level === "owner",
    };
  }

  // The event's assigned rep (events.msr_id) who is NOT an MSR keeps the implicit, event-scoped 'edit' grant of
  // has_event_permission() (2026-06-18-010). MSRs are handled above (view only, migration 402).
  const { data: assignedEvent } = await supabase
    .from("events")
    .select("msr_id")
    .eq("id", eventId)
    .single();

  if (assignedEvent?.msr_id && assignedEvent.msr_id === user.id) {
    return {
      event_id: eventId,
      user_id: user.id,
      permission_level: "edit" as PermissionLevel,
      is_owner: false,
      can_edit: true,
      can_manage: false,
    };
  }

  return null;
};

/**
 * Add a permission for a user to an event
 */
export const addEventPermission = async (
  input: AddEventPermissionInput,
): Promise<EventPermission> => {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from("event_permissions")
    .insert({
      event_id: input.event_id,
      user_id: input.user_id,
      permission_level: input.permission_level,
      granted_by: user?.id,
    })
    .select("*")
    .single();

  if (error) {
    console.error("[EventPermissions] Error adding permission:", error);
    throw error;
  }

  // Fetch user profile for the response
  const { data: profile } = await supabase
    .from("profiles")
    .select("id, full_name, email")
    .eq("id", input.user_id)
    .single();

  return {
    ...data,
    user: profile || undefined,
  };
};

/**
 * Update a user's permission level for an event
 */
export const updateEventPermission = async (
  eventId: UUID,
  userId: UUID,
  permissionLevel: PermissionLevel,
): Promise<EventPermission> => {
  const { data, error } = await supabase
    .from("event_permissions")
    .update({ permission_level: permissionLevel })
    .eq("event_id", eventId)
    .eq("user_id", userId)
    .select("*")
    .single();

  if (error) {
    console.error("[EventPermissions] Error updating permission:", error);
    throw error;
  }

  // Fetch user profile for the response
  const { data: profile } = await supabase
    .from("profiles")
    .select("id, full_name, email")
    .eq("id", userId)
    .single();

  return {
    ...data,
    user: profile || undefined,
  };
};

/**
 * Remove a user's permission from an event
 * Will fail if trying to remove the last owner
 */
export const removeEventPermission = async (
  eventId: UUID,
  userId: UUID,
): Promise<void> => {
  const { error } = await supabase
    .from("event_permissions")
    .delete()
    .eq("event_id", eventId)
    .eq("user_id", userId);

  if (error) {
    console.error("[EventPermissions] Error removing permission:", error);
    // Check if it's the "last owner" error
    if (error.message?.includes("last owner")) {
      throw new Error(
        "Cannot remove the last owner. At least one owner must remain.",
      );
    }
    throw error;
  }
};

/**
 * Get the count of owners for an event
 */
export const getEventOwnerCount = async (eventId: UUID): Promise<number> => {
  const { data, error } = await supabase
    .from("event_permissions")
    .select("id", { count: "exact" })
    .eq("event_id", eventId)
    .eq("permission_level", "owner");

  if (error) {
    console.error("[EventPermissions] Error counting owners:", error);
    throw error;
  }

  return data?.length || 0;
};

/**
 * Check if current user can edit an event
 */
export const canCurrentUserEditEvent = async (
  eventId: UUID,
): Promise<boolean> => {
  const access = await getUserEventAccess(eventId);
  return access?.can_edit || false;
};

/**
 * Check if current user can manage event permissions
 */
export const canCurrentUserManageEvent = async (
  eventId: UUID,
): Promise<boolean> => {
  const access = await getUserEventAccess(eventId);
  return access?.can_manage || false;
};

/**
 * Search users to share with (excludes users who already have permission)
 */
export const searchUsersForSharing = async (
  eventId: UUID,
  searchQuery: string,
  limit = 10,
): Promise<Array<{ id: UUID; full_name: string; email: string }>> => {
  // First get existing permission user IDs
  const { data: existingPermissions } = await supabase
    .from("event_permissions")
    .select("user_id")
    .eq("event_id", eventId);

  const excludeUserIds = existingPermissions?.map((p) => p.user_id) || [];

  // Search for users
  // Note: profiles.id IS the user_id (references auth.users.id)
  let query = supabase
    .from("profiles")
    .select("id, full_name, email")
    .eq("is_active", true)
    // A read-only viewer can never hold an event permission (has_event_permission refuses it, migration 1623).
    .neq("role", "readonly")
    .limit(limit);

  // Apply search filter
  if (searchQuery) {
    query = query.or(
      `full_name.ilike.*${searchQuery}*,email.ilike.*${searchQuery}*`,
    );
  }

  const { data, error } = await query;

  if (error) {
    console.error("[EventPermissions] Error searching users:", error);
    throw error;
  }

  // Filter out users who already have permission
  // profiles.id is the user's UUID (same as auth.users.id)
  const filteredUsers = (data || [])
    .filter((user) => !excludeUserIds.includes(user.id))
    .map((user) => ({
      id: user.id,
      full_name: user.full_name,
      email: user.email,
    }));

  return filteredUsers;
};
